import React, { FormEvent, useEffect, useState } from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { ModalComponent } from "components/ModalComponent";
import { ModalHeader } from "components/ModalHeader";
import { ModalTitle } from "components/ModalTitle";
import { FormField } from "components/FormField";
import { ConfirmPopup } from "components/ConfirmPopup";
import { CustomLoader } from "components/CustomLoader";
import squareLines from "assets/images/squareLines.png";
import { validateIntegerNumbers } from "constants/regExp";
import { SwitchButton } from "components/SwitchButton";
import { submitKey } from "config/constants";
import { useTypedDispatch } from "store";
import { useSelector } from "react-redux";
import * as selector from "store/selectors/assistantPages";

import { useFormik } from "formik";
import { updateAssistantPage } from "features/AssistantPages/AssistantPagesActions";
import { IAssistantPage, IBasePlan } from "api/getAssistantPages";
import {
  assistantEditValidationSchema,
  TAssistantEditValidationSchema,
} from "../schema";
import { TIERS } from "../constants";

type Props = {
  open: boolean;
  handleClose: () => void;
  handleOpenSuccess: () => void;
  handleOpenError: () => void;
};

const ViewAssistantPageEditModal = ({
  open,
  handleClose,
  handleOpenSuccess,
  handleOpenError,
}: Props) => {
  const theme = useTheme();
  const dispatch = useTypedDispatch();
  const assistantPage = useSelector(selector.assistantPage);

  const isLoading = useSelector(selector.isLoading);
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);

  const methods = useFormik<TAssistantEditValidationSchema>({
    validationSchema: assistantEditValidationSchema,
    enableReinitialize: true,
    initialValues: {
      workflowId: "",
      workflowVersion: "",
      limits: {
        basic: { enabled: true, maxProperties: 0, maxRowsPerScope: 0 },
        pro: { enabled: false, maxProperties: 0, maxRowsPerScope: 0 },
        team: { enabled: false, maxProperties: 0, maxRowsPerScope: 0 },
      },
    },
    onSubmit: (values) => {
      if (!assistantPage.code) {
        return;
      }
      dispatch(updateAssistantPage({ code: assistantPage.code, ...values }))
        .then(() => {
          handleOpenSuccess();
          handleClose();
          resetForm();
        })
        .catch(() => {
          handleOpenError();
        });
    },
  });

  const {
    values,
    handleSubmit,
    setFieldValue,
    setValues,
    resetForm,
    errors,
    touched,
  } = methods;

  const handleField = (
    field: keyof Pick<IAssistantPage, "workflowId" | "workflowVersion">,
    value: string
  ) => setFieldValue(field, value);

  const handleLimitField = (
    tier: (typeof TIERS)[number],
    field: keyof IBasePlan,
    value: boolean | number
  ) => setFieldValue(`limits.${tier}.${field}`, value);

  const handleConfirm = () => {
    setIsConfirmPopupOpen(false);
    resetForm();
    handleClose();
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit();
  };

  const handleCloseModal = () => {
    setIsConfirmPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsConfirmPopupOpen(false);
  };

  useEffect(() => {
    if (open && assistantPage) {
      setValues(assistantPage);
    }
  }, [open, assistantPage]);

  const isErrorVisible =
    Object.keys(touched).length > 0 && Object.keys(errors).length > 0;
  const error = errors.workflowId || errors.workflowVersion;

  return (
    <ModalComponent
      open={open}
      handleClose={handleCloseModal}
      boxStyles={{
        width: "90%",
        borderRadius: "6px",
        outline: "none",
        overflow: "hidden",
      }}
    >
      <ModalHeader
        isEditModal
        title="Edit Assistant Page"
        isErrorVisible={isErrorVisible}
        error={error ?? "Please, fill in all required fields"}
      />

      <Box
        sx={{
          width: "100%",
          overflow: "auto",
          pb: "40px",
        }}
      >
        <Box
          component={"form"}
          onSubmit={handleFormSubmit}
          onKeyDown={(event: React.KeyboardEvent<HTMLFormElement>) => {
            if (event.key === submitKey) {
              handleFormSubmit(event);
            }
          }}
          sx={{
            mt: "20px",
            padding: "0 40px",
          }}
        >
          <ModalTitle title="Workflow" />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: "35px",
            }}
          >
            <FormField
              value={values.workflowId}
              title="Workflow ID"
              formStyle={{ mr: 2 }}
              formKey="workflowId"
              isRequired
              handleChange={(value) => handleField("workflowId", value)}
              placeholder=""
              error={Boolean(errors.workflowId) && touched.workflowId}
              errorText={errors.workflowId}
            />
            <FormField
              value={values.workflowVersion}
              title="Workflow Version"
              formStyle={{ mr: 2 }}
              formKey="workflowVersion"
              isRequired
              handleChange={(value) => handleField("workflowVersion", value)}
              placeholder=""
              error={Boolean(errors.workflowVersion) && touched.workflowVersion}
              errorText={errors.workflowVersion}
            />
          </Box>

          <ModalTitle title="Limits" />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              mb: "35px",
              gap: "35px",
            }}
          >
            {TIERS.map((tier) => (
              <Box key={tier}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: "15px",
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      minWidth: "60px",
                    }}
                  >
                    {tier}
                  </Typography>
                  <SwitchButton
                    label={values.limits[tier].enabled ? "Enabled" : "Disabled"}
                    value={values.limits[tier].enabled}
                    handleChange={(_, checked) =>
                      handleLimitField(tier, "enabled", checked)
                    }
                    switchStyles={{
                      "& .MuiTypography-root": { fontSize: "14px", pl: "10px" },
                      "& .MuiSwitch-track": { bgcolor: "text.secondary" },
                      "& .MuiButtonBase-root.MuiSwitch-switchBase.Mui-disabled+.MuiSwitch-track":
                        { opacity: 1 },
                      "& .MuiButtonBase-root.MuiSwitch-switchBase.Mui-disabled":
                        { color: "common.white" },
                      "&.MuiFormControlLabel-root": {
                        mx: 0,
                        "& .MuiFormControlLabel-label.Mui-disabled": {
                          color: "rgba(61, 61, 61, .7)",
                        },
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    opacity: values.limits[tier].enabled ? 1 : 0.4,
                    pointerEvents: values.limits[tier].enabled
                      ? "auto"
                      : "none",
                  }}
                >
                  <FormField
                    value={String(values.limits[tier].maxProperties)}
                    title="Max Properties"
                    formStyle={{ mr: 2 }}
                    formKey={`${tier}_maxProperties`}
                    isNumber
                    numberOnChange={(value) =>
                      handleLimitField(tier, "maxProperties", Number(value))
                    }
                    validateValue={validateIntegerNumbers}
                    placeholder=""
                    disabled={!values.limits[tier].enabled}
                    error={
                      Boolean(errors.limits?.[tier]?.maxProperties) &&
                      Boolean(touched.limits?.[tier]?.maxProperties)
                    }
                  />
                  <FormField
                    value={String(values.limits[tier].maxRowsPerScope)}
                    title="Max Rows Per Scope"
                    formStyle={{ mr: 2 }}
                    formKey={`${tier}_maxRowsPerScope`}
                    isNumber
                    numberOnChange={(value) =>
                      handleLimitField(tier, "maxRowsPerScope", Number(value))
                    }
                    validateValue={validateIntegerNumbers}
                    placeholder=""
                    disabled={!values.limits[tier].enabled}
                    error={
                      Boolean(errors.limits?.[tier]?.maxRowsPerScope) &&
                      Boolean(touched.limits?.[tier]?.maxRowsPerScope)
                    }
                  />
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
              mr: 2,
            }}
          >
            <Box
              sx={{
                width: "50%",
                display: "flex",
              }}
            >
              <Button
                classes="light"
                onClick={handleCloseModal}
                fullWidth
                sx={{
                  mr: 2,
                  height: "45px",
                  fontSize: "14px",
                }}
              >
                CANCEL
              </Button>
              <Button
                type="submit"
                fullWidth
                sx={{
                  mr: 2,
                  height: "45px",
                  backgroundColor: theme.palette.secondary.main,
                  color: "white",
                  fontSize: "14px",
                }}
              >
                {isLoading ? (
                  <CustomLoader
                    size={20}
                    styles={{ color: "common.white" }}
                    boxStyles={{ display: "flex", alignItems: "center" }}
                  />
                ) : (
                  "SAVE"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <ConfirmPopup
        open={isConfirmPopupOpen}
        icon={squareLines}
        title="Cancel editing assistant page"
        subtitle="Are you sure you want to cancel editing assistant page?"
        cancelButtonTitle="NO, GO BACK"
        confirmButtonTitle="YES, CANCEL"
        handleClose={handleClosePopup}
        handleSubmit={handleConfirm}
      />
    </ModalComponent>
  );
};

export default ViewAssistantPageEditModal;
